import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { BetaResult } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useModelPicker } from "../ai/ModelPicker.js";

interface Props { projectId: string; }

const PROFILES = [
  { id: "casual", label: "Casual reader" },
  { id: "romance", label: "Romance reader" },
  { id: "mystery", label: "Mystery reader" },
  { id: "critical", label: "Critical reader" }
];

const GROUPS: { key: keyof BetaResult["reaction"]; label: string; icon: Parameters<typeof Icon>[0]["name"] }[] = [
  { key: "questions", label: "Questions", icon: "info" },
  { key: "expectations", label: "Expectations", icon: "sparkles" },
  { key: "suspicions", label: "Suspicions", icon: "search" },
  { key: "confusion", label: "Possible confusion", icon: "alert" },
  { key: "emotions", label: "Emotional reactions", icon: "heart" }
];

/**
 * Beta-Reader Simulation — reactions from a simulated first-time reader who knows
 * only the story up to a chosen chapter. Clearly AI analysis, not real reader
 * behaviour. Cached per chapter+profile.
 */
export function BetaReaderPanel({ projectId }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [chapters, setChapters] = useState<{ id: string; title: string }[]>([]);
  const [chapterId, setChapterId] = useState("");
  const [profile, setProfile] = useState("casual");
  const [result, setResult] = useState<BetaResult | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const m = await api.manuscript(projectId);
      const chs = [...m.chapters].sort((a, b) => a.position - b.position).map((c) => ({ id: c.id, title: c.title }));
      setChapters(chs);
      setChapterId((x) => x || chs[0]?.id || "");
    } catch { /* ignore */ }
  }, [projectId]);
  useEffect(() => { void load(); }, [load]);

  const run = async () => {
    if (!chapterId) return;
    const choice = await pickModel({ task: "general-chat" });
    if (!choice) return;
    setRunning(true);
    try {
      setResult(await api.betaReader(projectId, chapterId, profile, undefined, choice));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Simulation failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head"><Icon name="users" size={16} style={{ color: "var(--accent)" }} /><h3>Beta Reader</h3></div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 10px" }}>
          A simulated first-time reader who knows the story only up to the chosen chapter — no spoilers from later. AI analysis, not real reader behaviour.
        </p>
        <div className="row gap-2" style={{ flexWrap: "wrap", alignItems: "center" }}>
          <select className="select" value={profile} onChange={(e) => setProfile(e.target.value)} style={{ maxWidth: 180 }}>
            {PROFILES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <span className="faint" style={{ fontSize: 12 }}>after</span>
          <select className="select" value={chapterId} onChange={(e) => setChapterId(e.target.value)} style={{ maxWidth: 240 }}>
            {chapters.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <button className="btn btn-sm btn-primary" onClick={() => void run()} disabled={running || !chapterId}>
            <Icon name={running ? "clock" : "sparkles"} size={13} className={running ? "spin" : ""} /> {running ? "Reading…" : "Simulate"}
          </button>
        </div>
      </section>

      {result && (
        <section className="card insight-card">
          <div className="insight-card-head"><Icon name="book" size={16} style={{ color: "var(--accent)" }} /><h3>After "{result.chapterTitle}"</h3><span className="spacer" /><span className="faint" style={{ fontSize: 11 }}>{result.profile} reader{result.cached ? " · cached" : ""}</span></div>
          {GROUPS.map((g) => {
            const items = result.reaction[g.key] as string[];
            if (!items || items.length === 0) return null;
            return (
              <div key={g.key} className="beta-group">
                <div className="beta-group-head"><Icon name={g.icon} size={13} /> {g.label}</div>
                <ul className="beta-list">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
              </div>
            );
          })}
          {result.reaction.trust.length > 0 && (
            <div className="beta-group">
              <div className="beta-group-head"><Icon name="users" size={13} /> Trust in characters</div>
              <div className="intel-chiprow">
                {result.reaction.trust.map((t, i) => <span key={i} className="chip">{t.character}: {t.feeling}</span>)}
              </div>
            </div>
          )}
          {GROUPS.every((g) => (result.reaction[g.key] as string[]).length === 0) && result.reaction.trust.length === 0 && (
            <p className="faint" style={{ fontSize: 12 }}>No notable reactions for this chapter.</p>
          )}
        </section>
      )}
    </div>
  );
}
